import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { MAP_STYLES } from '@strava/ui/Map';
import ActivityRoutesNormalXsmall from '@strava/icons/ActivityRoutesNormalXsmall';
import ActionsDownloadNormalXsmall from '@strava/icons/ActionsDownloadNormalXsmall';
import ActionsFullScreenEnterNormalXsmall from '@strava/icons/ActionsFullScreenEnterNormalXsmall';
import ActionsFullScreenExitNormalXsmall from '@strava/icons/ActionsFullScreenExitNormalXsmall';
import styles from './MapControls.scss';

const MapControls = ({
  showCreateRoute,
  activityId,
  showGpxDownload,
  showFullScreenToggle,
  mapStyle,
  handleMapStyleChange,
  hasPrivacyZones
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const isSafari =
      /safari/.test(navigator.userAgent.toLowerCase()) &&
      !/chrome/.test(navigator.userAgent.toLowerCase());

    if (isFullscreen) {
      document.body.classList.add(isSafari ? 'fauxscreen' : 'fullscreen');
    } else {
      document.body.classList.remove(isSafari ? 'fauxscreen' : 'fullscreen');
    }
  }, [isFullscreen]);

  const handleFullscreenToggleClick = () => {
    const toggleFullscreenEvent = new CustomEvent('toggleFullscreen');
    document.dispatchEvent(toggleFullscreenEvent);

    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={styles.mapControlContainer}>
      <div className={styles.mapControls}>
        {showCreateRoute && (
          <a
            className={styles.button}
            id="create-route"
            href={`/activities/${activityId}/route`}
          >
            <ActivityRoutesNormalXsmall />
            <span>
              {I18n.t('strava.maps.mapbox_gl.custom_control.create_route')}
            </span>
          </a>
        )}

        {showGpxDownload && (
          <a
            className={styles.button}
            id="gpx-download"
            href={`/activities/${activityId}/export_gpx`}
          >
            <ActionsDownloadNormalXsmall />
            <span>
              {I18n.t('strava.maps.mapbox_gl.custom_control.export_gpx')}
            </span>
          </a>
        )}

        {/* TODO select styling is WIP, the options list is currently unstyled */}
        <select
          className={styles.mapStyleSelect}
          value={mapStyle}
          onChange={handleMapStyleChange}
        >
          {Object.keys(MAP_STYLES).map((style) => (
            <option value={style} key={style}>
              {I18n.t(
                `strava.maps.mapbox_gl.custom_control.map_style.${style}`
              )}
            </option>
          ))}
        </select>

        {showFullScreenToggle && (
          <button
            className={`${styles.button} ${styles.fullScreenToggle}`}
            onClick={handleFullscreenToggleClick}
            label={I18n.t(
              isFullscreen
                ? 'strava.maps.mapbox_gl.custom_control.exit_fullscreen_mode'
                : 'strava.maps.mapbox_gl.custom_control.enter_fullscreen_mode'
            )}
          >
            {isFullscreen ? (
              <ActionsFullScreenExitNormalXsmall />
            ) : (
              <ActionsFullScreenEnterNormalXsmall />
            )}
          </button>
        )}
      </div>

      {hasPrivacyZones && (
        <div className={styles.privacyLegend}>
          <div className={styles.legendItem}>
            <div className={`${styles.line} ${styles.visible}`} />
            <div>
              {I18n.t(
                'strava.maps.mapbox_gl.custom_control.route_visible_label'
              )}
            </div>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.line} ${styles.hidden}`} />
            <div>
              {I18n.t(
                'strava.maps.mapbox_gl.custom_control.route_hidden_label'
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MapControls.propTypes = {
  showCreateRoute: PropTypes.bool,
  activityId: PropTypes.number.isRequired,
  showGpxDownload: PropTypes.bool,
  showFullScreenToggle: PropTypes.bool,
  mapStyle: PropTypes.string.isRequired,
  handleMapStyleChange: PropTypes.func.isRequired,
  hasPrivacyZones: PropTypes.bool.isRequired
};

MapControls.defaultProps = {
  showCreateRoute: true,
  showGpxDownload: true,
  showFullScreenToggle: false
};

export default MapControls;
